import React, { useEffect, useState } from 'react';
import { Wallet, Pencil, Banknote, Scissors, ClipboardList, HandCoins, Trash2, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Modal, Badge, Th, Td, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate, ROLE_LABELS, canDelete } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import SalaryStatementPrint from '../components/SalaryStatementPrint';
import { downloadElementAsPdf } from '../lib/pdf';

const monthLabel = (m) => (m ? new Date(`${m}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : '—');

export default function Salary() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [ov, setOv] = useState(null);
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState(null);
  const [pay, setPay] = useState(null);
  const [adv, setAdv] = useState(null);
  const [advances, setAdvances] = useState([]);
  const [statement, setStatement] = useState(null);
  const [editSal, setEditSal] = useState(null);
  const [tailorPays, setTailorPays] = useState(null);

  const load = () => {
    api.get('/salary/overview', { params: { month } }).then((r) => setOv(r.data));
    api.get('/salaries').then((r) => setHistory(r.data));
    api.get('/advances').then((r) => setAdvances(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [month]);

  const delSalary = async (id) => {
    if (!window.confirm('Delete this salary payment? The linked expense and advance deduction will be reversed.')) return;
    try { await api.delete(`/salaries/${id}`); toast.success('Salary record deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delAdvance = async (id) => {
    if (!window.confirm('Delete this advance? The linked expense will also be removed.')) return;
    try { await api.delete(`/advances/${id}`); toast.success('Advance deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!ov) return <div className="text-slate-400 animate-pulse">Loading salary data…</div>;

  return (
    <div>
      <PageHeader title="Salary" subtitle={`Employee salaries, advances & payouts · ${monthLabel(ov.month)}`}
        actions={<div className="flex items-end gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="salary-month-picker" />
          <Button variant="outline" onClick={() => setMonth(new Date().toISOString().slice(0, 7))} data-testid="salary-this-month-btn">This Month</Button>
        </div>} />

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Monthly Staff (Owner / Admin / Sales)</p>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Employee</Th><Th>Role</Th><Th right>Monthly Salary</Th><Th>Salary Day</Th><Th right>Advance</Th><Th right>Paid in {month}</Th><Th right>Total Paid</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ov.monthly.map((m) => (
                <tr key={m.userId} data-testid="salary-monthly-row" className="hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-800">{m.name}</Td>
                  <Td><Badge className="bg-slate-100 border-slate-200 text-slate-600">{ROLE_LABELS[m.role] || m.role}</Badge></Td>
                  <Td right className="num">{fmtMoney(m.monthlySalary)}</Td>
                  <Td>{m.salaryDay ? `${m.salaryDay} of month` : '—'}</Td>
                  <Td right className={`num ${m.advanceOutstanding > 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>{fmtMoney(m.advanceOutstanding)}</Td>
                  <Td right className={`num font-semibold ${m.isPaid ? 'text-emerald-600' : 'text-[#B45309]'}`}>{fmtMoney(m.paidThisMonth)}</Td>
                  <Td right className="num">{fmtMoney(m.totalPaid)}</Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {m.isPaid && <Badge className="bg-emerald-50 border-emerald-200 text-emerald-700 mr-2" data-testid="salary-paid-badge">Paid till {fmtDate(m.nextDueDate)}</Badge>}
                    <button onClick={() => setStatement({ kind: 'monthly', key: m.userId, name: m.name })} className="text-slate-400 hover:text-[#1E3A8A] p-1" title="Month-wise statement" data-testid="statement-monthly-btn"><FileText size={15} /></button>
                    <button onClick={() => setConfig({ kind: 'monthly', ...m })} className="text-slate-400 hover:text-blue-600 p-1" title="Set salary" data-testid="edit-salary-btn"><Pencil size={15} /></button>
                    <Button size="sm" variant="outline" className="ml-2" onClick={() => setAdv({ kind: 'monthly', employeeKey: m.userId, name: m.name })} data-testid="advance-monthly-btn"><HandCoins size={13} /> Advance</Button>
                    {!m.isPaid && <Button size="sm" variant="success" className="ml-2" onClick={() => setPay({ kind: 'monthly', ...m })} data-testid="pay-salary-btn"><Banknote size={13} /> Pay</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Cutters (Per Roll) · figures for {monthLabel(ov.month)}</p>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Cutter</Th><Th right>Rate / Roll</Th><Th right>Rolls (month)</Th><Th right>Earned (month)</Th><Th right>Paid (month)</Th><Th right>Advance</Th><Th right>Pending (total)</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ov.cutters.map((c) => (
                <tr key={c.userId} data-testid="salary-cutter-row" className="hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-800">{c.name}</Td>
                  <Td right className="num">{fmtMoney(c.rollRate)}</Td>
                  <Td right className="num">{fmtNum(c.rolls)}</Td>
                  <Td right className="num">{fmtMoney(c.earned)}</Td>
                  <Td right className="num text-emerald-600">{fmtMoney(c.paid)}</Td>
                  <Td right className={`num ${c.advanceOutstanding > 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>{fmtMoney(c.advanceOutstanding)}</Td>
                  <Td right className={`num font-semibold ${c.pending > 0 ? 'text-[#B45309]' : 'text-emerald-600'}`}>{fmtMoney(c.pending)}</Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setStatement({ kind: 'cutter', key: c.userId, name: c.name })} className="text-slate-400 hover:text-[#1E3A8A] p-1" title="Month-wise statement" data-testid="statement-cutter-btn"><FileText size={15} /></button>
                    <button onClick={() => setConfig({ kind: 'cutter', ...c })} className="text-slate-400 hover:text-blue-600 p-1" title="Set roll rate" data-testid="edit-rollrate-btn"><Pencil size={15} /></button>
                    <Button size="sm" variant="outline" className="ml-2" onClick={() => setAdv({ kind: 'cutter', employeeKey: c.userId, name: c.name })} data-testid="advance-cutter-btn"><HandCoins size={13} /> Advance</Button>
                    <Button size="sm" variant="success" className="ml-2" onClick={() => setPay({ kind: 'cutter', ...c })} data-testid="pay-cutter-salary-btn"><Banknote size={13} /> Pay</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!ov.cutters.length && <EmptyState title="No cutters" icon={Scissors} />}
      </Card>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Tailors (Per Dozen) · figures for {monthLabel(ov.month)}</p>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Tailor</Th><Th right>Rate / Dozen</Th><Th right>Earned (month)</Th><Th right>Paid (month)</Th><Th right>Advance</Th><Th right>Pending (total)</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ov.tailors.map((t) => (
                <tr key={t.tailorId} data-testid="salary-tailor-row" className="hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-800">{t.name}</Td>
                  <Td right className="num">{fmtMoney(t.rate)}</Td>
                  <Td right className="num">{fmtMoney(t.earned)}</Td>
                  <Td right className="num text-emerald-600">{fmtMoney(t.paid)}</Td>
                  <Td right className={`num ${t.advanceOutstanding > 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>{fmtMoney(t.advanceOutstanding)}</Td>
                  <Td right className={`num font-semibold ${t.pending > 0 ? 'text-[#B45309]' : 'text-emerald-600'}`}>{fmtMoney(t.pending)}</Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setStatement({ kind: 'tailor', key: t.tailorId, name: t.name })} className="text-slate-400 hover:text-[#1E3A8A] p-1" title="Month-wise statement" data-testid="statement-tailor-btn"><FileText size={15} /></button>
                    <Button size="sm" variant="outline" onClick={() => setTailorPays(t)} data-testid="edit-tailor-salary-btn"><Pencil size={13} /> Edit Salary</Button>
                    <Button size="sm" variant="outline" className="ml-2" onClick={() => setAdv({ kind: 'tailor', employeeKey: t.tailorId, name: t.name })} data-testid="advance-tailor-btn"><HandCoins size={13} /> Advance</Button>
                    <Button size="sm" variant="success" className="ml-2" onClick={() => setPay({ kind: 'tailor', ...t })} data-testid="pay-tailor-salary-btn"><Banknote size={13} /> Pay</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!ov.tailors.length && <EmptyState title="No tailors yet" description="Add tailors from the Tailors page." icon={ClipboardList} />}
      </Card>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Advance History</p>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th>Employee</Th><Th>Type</Th><Th right>Amount</Th><Th right>Recovered</Th><Th right>Outstanding</Th><Th>Given By</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {advances.map((a) => (
                <tr key={a.id} data-testid="advance-history-row" className="hover:bg-slate-50/60">
                  <Td>{fmtDate(a.date)}</Td>
                  <Td className="font-medium text-slate-800">{a.name}</Td>
                  <Td><Badge className="bg-slate-100 border-slate-200 text-slate-600 capitalize">{a.kind}</Badge></Td>
                  <Td right className="num font-semibold">{fmtMoney(a.amount)}</Td>
                  <Td right className="num text-emerald-600">{fmtMoney(a.recovered || 0)}</Td>
                  <Td right className={`num font-semibold ${a.outstanding > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmtMoney(a.outstanding)}</Td>
                  <Td className="text-xs text-slate-500">{a.addedBy}</Td>
                  <td className="px-3 py-2.5 text-right">
                    {canDelete(user.role) && <button onClick={() => delAdvance(a.id)} className="text-slate-400 hover:text-rose-600 p-1" data-testid="delete-advance-btn"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!advances.length && <EmptyState title="No advances given yet" icon={HandCoins} />}
      </Card>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Salary Payment History (Monthly Staff)</p>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th>Employee</Th><Th>Month</Th><Th right>Salary Amount</Th><Th right>Advance Deducted</Th><Th right>Net Cash Paid</Th><Th>Notes</Th><Th>Paid By</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((s) => (
                <tr key={s.id} data-testid="salary-history-row" className="hover:bg-slate-50/60">
                  <Td>{fmtDate(s.date)}</Td>
                  <Td className="font-medium text-slate-800">{s.userName}</Td>
                  <Td className="num">{s.month}</Td>
                  <Td right className="num font-semibold">{fmtMoney(s.amount)}</Td>
                  <Td right className={`num ${s.advanceDeduction > 0 ? 'text-rose-600' : 'text-slate-400'}`} data-testid="salary-history-deduction">{fmtMoney(s.advanceDeduction || 0)}</Td>
                  <Td right className="num font-semibold text-emerald-600" data-testid="salary-history-net">{fmtMoney(s.netPaid ?? (s.amount - (s.advanceDeduction || 0)))}</Td>
                  <Td className="text-slate-500">{s.notes || '—'}</Td>
                  <Td className="text-xs text-slate-500">{s.paidBy}</Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {canDelete(user.role) && <>
                      <button onClick={() => setEditSal(s)} className="text-slate-400 hover:text-blue-600 p-1" title="Edit salary payment" data-testid="edit-salary-payment-btn"><Pencil size={14} /></button>
                      <button onClick={() => delSalary(s.id)} className="text-slate-400 hover:text-rose-600 p-1" title="Delete salary payment" data-testid="delete-salary-btn"><Trash2 size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!history.length && <EmptyState title="No salary payments yet" icon={Wallet} />}
      </Card>

      {config && <ConfigModal cfg={config} onClose={() => setConfig(null)} onSaved={() => { setConfig(null); load(); }} />}
      {pay && <PayModal p={pay} onClose={() => setPay(null)} onSaved={() => { setPay(null); load(); }} />}
      {adv && <AdvanceModal a={adv} onClose={() => setAdv(null)} onSaved={() => { setAdv(null); load(); }} />}
      {statement && <StatementModal s={statement} onClose={() => setStatement(null)} />}
      {editSal && <EditSalaryModal s={editSal} outstanding={(ov.monthly.find((m) => m.userId === editSal.userId)?.advanceOutstanding || 0)}
        onClose={() => setEditSal(null)} onSaved={() => { setEditSal(null); load(); }} />}
      {tailorPays && <TailorPaymentsModal t={tailorPays} onClose={() => { setTailorPays(null); load(); }} />}
    </div>
  );
}

function TailorPaymentsModal({ t, onClose }) {
  const [rows, setRows] = useState([]);
  const [outstanding, setOutstanding] = useState(t.advanceOutstanding || 0);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get('/tailor-payments').then((r) => setRows(r.data.filter((p) => p.tailorId === t.tailorId)));
    api.get('/advances').then((r) => setOutstanding(
      r.data.filter((a) => a.kind === 'tailor' && a.employeeKey === t.tailorId)
        .reduce((s, a) => s + (a.outstanding || 0), 0)
    )).catch(() => {});
  };
  useEffect(load, [t.tailorId]);

  const del = async (p) => {
    if (!window.confirm(`Delete the payment of ${fmtMoney(p.amount)} dated ${fmtDate(p.date)}? Any advance deduction in this entry will be added back to the outstanding advance.`)) return;
    try { await api.delete(`/tailor-payments/${p.id}`); toast.success('Payment deleted · advance reversed'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Modal open size="lg" title={`Salary Payments · ${t.name}`} onClose={onClose}
      footer={<Button variant="outline" onClick={onClose}>Close</Button>}>
      {editing ? (
        <EditTailorPaymentForm p={editing} outstanding={outstanding}
          onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 text-sm">
            <span className="text-slate-500">Rate <span className="num font-semibold text-slate-700">{fmtMoney(t.rate)}</span>/dozen · Pending <span className="num font-semibold text-[#B45309]">{fmtMoney(t.pending)}</span></span>
            <span className="text-slate-500">Outstanding Advance <span className="num font-semibold text-rose-600" data-testid="tailor-pay-outstanding">{fmtMoney(outstanding)}</span></span>
          </div>
          <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr><Th>Date</Th><Th right>Amount</Th><Th right>Advance Deducted</Th><Th right>Net Cash Paid</Th><Th>Method</Th><Th>Notes</Th><Th className="text-right">Actions</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => (
                  <tr key={p.id} data-testid="tailor-payment-row" className="hover:bg-slate-50/60">
                    <Td>{fmtDate(p.date)}</Td>
                    <Td right className="num font-semibold">{fmtMoney(p.amount)}</Td>
                    <Td right className={`num ${p.advanceDeduction > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmtMoney(p.advanceDeduction || 0)}</Td>
                    <Td right className="num font-semibold text-emerald-600">{fmtMoney(p.netPaid ?? (p.amount - (p.advanceDeduction || 0)))}</Td>
                    <Td>{p.paymentMethod || 'Cash'}</Td>
                    <Td className="text-slate-500">{p.notes || '—'}</Td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(p)} className="text-slate-400 hover:text-blue-600 p-1" title="Edit payment" data-testid="edit-tailor-payment-btn"><Pencil size={14} /></button>
                      <button onClick={() => del(p)} className="text-slate-400 hover:text-rose-600 p-1" title="Delete payment" data-testid="delete-tailor-payment-btn"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && <EmptyState title="No payments recorded for this tailor" icon={Wallet} />}
        </>
      )}
    </Modal>
  );
}

function EditTailorPaymentForm({ p, outstanding, onCancel, onSaved }) {
  const [f, setF] = useState({
    date: p.date, amount: p.amount, deduction: p.advanceDeduction || 0, notes: p.notes || '',
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const amountNum = Number(f.amount) || 0;
  const available = Math.round((outstanding + (p.advanceDeduction || 0)) * 1000) / 1000;
  const dedNum = Math.min(Number(f.deduction) || 0, available, amountNum);
  const net = Math.max(0, Math.round((amountNum - dedNum) * 1000) / 1000);
  const save = async () => {
    try {
      await api.put(`/tailor-payments/${p.id}`, { amount: amountNum, date: f.date, advanceDeduction: dedNum, notes: f.notes });
      toast.success('Payment updated'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input label="Payment Date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} data-testid="edit-tailor-pay-date" />
        <Input label="Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="edit-tailor-pay-amount" />
      </div>
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-amber-800 font-medium">Advance Available to Deduct</span>
          <span className="num font-bold text-rose-600" data-testid="edit-tailor-pay-available">{fmtMoney(available)}</span>
        </div>
        <Input label="Advance Deduction (KWD)" type="number" step="0.001" value={f.deduction}
          onChange={(e) => set('deduction', e.target.value)} data-testid="edit-tailor-pay-deduction" />
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => set('deduction', Math.min(available, amountNum))}>Deduct Full</Button>
          <Button size="sm" variant="outline" onClick={() => set('deduction', 0)}>No Deduction</Button>
        </div>
      </div>
      <Input label="Notes" value={f.notes} onChange={(e) => set('notes', e.target.value)} data-testid="edit-tailor-pay-notes" />
      <div className="flex items-center justify-between text-sm border-t border-slate-100 pt-3">
        <div>
          <span className="text-slate-500">Net Cash Paid</span>
          <span className="block text-[11px] text-slate-400 num" data-testid="edit-tailor-pay-formula">
            {amountNum.toFixed(3)} − {dedNum.toFixed(3)} (advance) = {net.toFixed(3)}
          </span>
        </div>
        <span className="num font-bold text-emerald-600 text-base">{fmtMoney(net)}</span>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} data-testid="cancel-edit-tailor-pay-btn">Back to list</Button>
        <Button onClick={save} data-testid="save-edit-tailor-pay-btn"><Banknote size={15} /> Save Changes</Button>
      </div>
    </div>
  );
}

function EditSalaryModal({ s, outstanding, onClose, onSaved }) {
  const [f, setF] = useState({
    date: s.date, month: s.month, amount: s.amount,
    deduction: s.advanceDeduction || 0, notes: s.notes || '',
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const amountNum = Number(f.amount) || 0;
  // this record's own deduction is released back before re-applying, so it is available again
  const available = Math.round((outstanding + (s.advanceDeduction || 0)) * 1000) / 1000;
  const dedNum = Math.min(Number(f.deduction) || 0, available, amountNum);
  const net = Math.max(0, Math.round((amountNum - dedNum) * 1000) / 1000);
  const save = async () => {
    try {
      await api.put(`/salaries/${s.id}`, {
        amount: amountNum, date: f.date, month: f.month,
        advanceDeduction: dedNum, notes: f.notes,
      });
      toast.success('Salary payment updated'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={`Edit Salary Payment · ${s.userName}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-edit-salary-btn"><Banknote size={15} /> Save Changes</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Salary Date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} data-testid="edit-salary-date-input" />
          <Input label="For Month" type="month" value={f.month} onChange={(e) => set('month', e.target.value)} data-testid="edit-salary-month-input" />
        </div>
        <Input label="Salary Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="edit-salary-amount-input" />
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-800 font-medium">Advance Available to Deduct</span>
            <span className="num font-bold text-rose-600" data-testid="edit-salary-available-advance">{fmtMoney(available)}</span>
          </div>
          <Input label="Advance Deduction (KWD)" type="number" step="0.001" value={f.deduction}
            onChange={(e) => set('deduction', e.target.value)} data-testid="edit-salary-deduction-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => set('deduction', Math.min(available, amountNum))}>Deduct Full</Button>
            <Button size="sm" variant="outline" onClick={() => set('deduction', 0)}>No Deduction</Button>
          </div>
        </div>
        <Input label="Notes" value={f.notes} onChange={(e) => set('notes', e.target.value)} data-testid="edit-salary-notes-input" />
        <div className="flex items-center justify-between text-sm border-t border-slate-100 pt-3">
          <div>
            <span className="text-slate-500">Net Cash Paid</span>
            <span className="block text-[11px] text-slate-400 num" data-testid="edit-salary-net-formula">
              {amountNum.toFixed(3)} − {dedNum.toFixed(3)} (advance) = {net.toFixed(3)}
            </span>
          </div>
          <span className="num font-bold text-emerald-600 text-base" data-testid="edit-salary-net-amount">{fmtMoney(net)}</span>
        </div>
        <p className="text-xs text-slate-400">The old advance deduction is reversed and the new one re-applied; the linked salary expense entry is updated too.</p>
      </div>
    </Modal>
  );
}

function StatementModal({ s, onClose }) {
  const [st, setSt] = useState(null);
  const [company, setCompany] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const unitLabel = s.kind === 'cutter' ? 'Rolls' : s.kind === 'tailor' ? 'Dozens' : 'Days';

  useEffect(() => {
    api.get('/salary/statement', { params: { kind: s.kind, key: s.key } }).then((r) => setSt(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get('/settings').then((r) => setCompany(r.data?.company)).catch(() => {});
  }, [s.kind, s.key]);

  const download = async () => {
    setDownloading(true);
    try {
      const name = (s.name || 'employee').replace(/[^\w\u0600-\u06FF\s-]/g, '').trim().replace(/\s+/g, '-');
      await downloadElementAsPdf(document.getElementById('salary-statement-print'), `${name}-Salary-Statement.pdf`);
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };

  return (
    <Modal open size="lg" title={`${s.name} · Month-wise Salary Statement`} onClose={onClose}
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={download} disabled={downloading || !st} data-testid="download-statement-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      {!st ? <p className="text-slate-400 animate-pulse">Loading statement…</p> : (
        <div className="space-y-5">
          <div className="grid grid-cols-4 gap-3">
            {[['Total Earned', fmtMoney(st.totals.earned)], ['Total Paid', fmtMoney(st.totals.paid)],
              ['Advance Given', fmtMoney(st.totals.advanceGiven)], ['Advance Outstanding', fmtMoney(st.advanceOutstanding)]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <p className="text-[10px] uppercase text-slate-400">{l}</p>
                <p className="num font-bold text-slate-800">{v}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Month</Th><Th right>{unitLabel}</Th><Th right>Earned / Salary</Th><Th right>Advance Given</Th><Th right>Adv. Deducted</Th><Th right>Paid</Th><Th right>Net Cash</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {st.months.map((r) => (
                  <tr key={r.month} data-testid="statement-month-row" className="hover:bg-slate-50/60">
                    <Td className="font-medium text-slate-800">{monthLabel(r.month)}</Td>
                    <Td right className="num">{s.kind === 'monthly' ? '—' : fmtNum(r.units)}</Td>
                    <Td right className="num">{fmtMoney(r.earned)}</Td>
                    <Td right className={`num ${r.advanceGiven > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmtMoney(r.advanceGiven)}</Td>
                    <Td right className="num">{fmtMoney(r.advanceDeducted)}</Td>
                    <Td right className="num text-emerald-600">{fmtMoney(r.paid)}</Td>
                    <Td right className="num font-semibold">{fmtMoney(r.netPaid)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!st.months.length && <EmptyState title="No salary activity yet" icon={Wallet} />}

          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Details</p>
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr><Th>Date</Th><Th>Type</Th><Th>Description</Th><Th right>Qty</Th><Th right>Amount</Th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {st.details.map((d, i) => (
                    <tr key={i} data-testid="statement-detail-row" className="hover:bg-slate-50/60">
                      <Td>{fmtDate(d.date)}</Td>
                      <Td><Badge className="bg-slate-100 border-slate-200 text-slate-600">{d.type}</Badge></Td>
                      <Td className="text-slate-600">{d.description}</Td>
                      <Td right className="num">{d.units ? fmtNum(d.units) : '—'}</Td>
                      <Td right className="num font-medium">{fmtMoney(d.earned ?? d.amount ?? d.advanceGiven ?? 0)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ position: 'fixed', left: '-20000px', top: 0 }}>
            <div id="salary-statement-print">
              <SalaryStatementPrint st={st} company={company} unitLabel={unitLabel} />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function AdvanceModal({ a, onClose, onSaved }) {
  const [f, setF] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.post('/advances', { kind: a.kind, employeeKey: a.employeeKey, amount: Number(f.amount), date: f.date, notes: f.notes });
      toast.success('Advance recorded'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={`Give Advance · ${a.name}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-advance-btn"><HandCoins size={15} /> Record Advance</Button></>}>
      <div className="space-y-4">
        <Input label="Date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} data-testid="advance-date-input" />
        <Input label="Advance Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="advance-amount-input" />
        <Input label="Notes" value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        <p className="text-xs text-slate-400">The advance will be deducted (fully or partially — your choice) when paying salary.</p>
      </div>
    </Modal>
  );
}

function ConfigModal({ cfg, onClose, onSaved }) {
  const monthly = cfg.kind === 'monthly';
  const [val, setVal] = useState(monthly ? cfg.monthlySalary : cfg.rollRate);
  const [day, setDay] = useState(cfg.salaryDay || '');
  const save = async () => {
    try {
      const payload = monthly ? { monthlySalary: Number(val) } : { rollRate: Number(val) };
      if (monthly && day) payload.salaryDay = Number(day);
      await api.put(`/users/${cfg.userId}/salary-config`, payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={`${cfg.name} · ${monthly ? 'Monthly Salary' : 'Rate per Roll'}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-salary-config-btn">Save</Button></>}>
      <div className="space-y-4">
        <Input label={monthly ? 'Monthly Salary (KWD)' : 'Rate per Roll (KWD)'} type="number" step="0.001" value={val} onChange={(e) => setVal(e.target.value)} data-testid="salary-config-input" />
        {monthly && <Input label="Salary Day (day of month, 1–31)" type="number" min="1" max="31" value={day} onChange={(e) => setDay(e.target.value)} data-testid="salary-day-input" />}
      </div>
    </Modal>
  );
}

function PayModal({ p, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const defaultDate = (p.kind === 'monthly' && p.salaryDay)
    ? `${today.slice(0, 7)}-${String(p.salaryDay).padStart(2, '0')}`
    : today;
  const outstanding = p.advanceOutstanding || 0;
  const [f, setF] = useState({
    date: defaultDate,
    month: today.slice(0, 7),
    amount: p.kind === 'monthly' ? (p.monthlySalary || '') : (p.pending > 0 ? p.pending : ''),
    deduction: 0,
    notes: '',
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const amountNum = Number(f.amount) || 0;
  const dedNum = Math.min(Number(f.deduction) || 0, outstanding, amountNum);
  const net = Math.max(0, amountNum - dedNum);
  const save = async () => {
    try {
      const amount = Number(f.amount);
      const advanceDeduction = dedNum;
      if (p.kind === 'monthly') {
        await api.post('/salaries', { userId: p.userId, month: f.month, amount, date: f.date, notes: f.notes, advanceDeduction });
      } else if (p.kind === 'cutter') {
        await api.post('/cutter-payments', { cutterUserId: p.userId, amount, notes: f.notes || `Salary payout ${f.date}`, advanceDeduction });
      } else {
        await api.post('/tailor-payments', { tailorId: p.tailorId, amount, notes: f.notes || `Salary payout ${f.date}`, advanceDeduction });
      }
      toast.success('Payment recorded'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={`Pay Salary · ${p.name}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="success" onClick={save} data-testid="confirm-pay-salary-btn"><Banknote size={15} /> Record Payment</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Salary Date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} data-testid="salary-date-input" />
          {p.kind === 'monthly' && <Input label="For Month" type="month" value={f.month} onChange={(e) => set('month', e.target.value)} data-testid="salary-month-input" />}
        </div>
        <Input label="Salary Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="salary-amount-input" />
        {outstanding > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-amber-800 font-medium">Outstanding Advance</span>
              <span className="num font-bold text-rose-600">{fmtMoney(outstanding)}</span>
            </div>
            <Input label="Deduct Advance (KWD) — full or partial" type="number" step="0.001" value={f.deduction}
              onChange={(e) => set('deduction', e.target.value)} data-testid="advance-deduction-input" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => set('deduction', Math.min(outstanding, amountNum))} data-testid="deduct-full-btn">Deduct Full</Button>
              <Button size="sm" variant="outline" onClick={() => set('deduction', 0)}>No Deduction</Button>
            </div>
          </div>
        )}
        <Input label="Notes" value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        <div className="flex items-center justify-between text-sm border-t border-slate-100 pt-3">
          <div>
            <span className="text-slate-500">Net Cash to Pay</span>
            <span className="block text-[11px] text-slate-400 num" data-testid="net-pay-formula">
              {amountNum.toFixed(3)} − {dedNum.toFixed(3)} (advance) = {net.toFixed(3)}
            </span>
          </div>
          <span className="num font-bold text-emerald-600 text-base" data-testid="net-pay-amount">{fmtMoney(net)}</span>
        </div>
        {p.kind !== 'monthly' && <p className="text-xs text-slate-400">Pending earnings: <span className="font-semibold text-slate-600 num">{fmtMoney(p.pending)}</span></p>}
      </div>
    </Modal>
  );
}
